import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CollectionsIcon from '@mui/icons-material/Collections';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import {
  CompetitorsResponse,
  MoverStats,
  SerpResponse,
  TransparencyResponse,
} from '../types';

const pct = (v: number | null | undefined): string =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

interface Props {
  service: AuctionIntelligenceService;
  campaignId?: string;
  adGroupId?: string;
  title: string;
  store: string;
  serpKeywordSuggestion?: string;
}

// Competitor movement for one entity (or the whole account when no ids given),
// from uploaded auction-insights data — plus on-demand live evidence: a real
// geo-targeted SERP sample and the competitor's Transparency Center creatives.
export default function CompetitorPanel({
  service,
  campaignId,
  adGroupId,
  title,
  store,
  serpKeywordSuggestion,
}: Props) {
  const [data, setData] = useState<CompetitorsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [serpKeyword, setSerpKeyword] = useState(serpKeywordSuggestion ?? '');
  const [serp, setSerp] = useState<SerpResponse | null>(null);
  const [serpBusy, setSerpBusy] = useState(false);
  const [creatives, setCreatives] = useState<TransparencyResponse | null>(null);
  const [creativesFor, setCreativesFor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    service
      .getCompetitors({ campaignId, adGroupId, weeks: 12 })
      .then(resp => {
        if (!cancelled) setData(resp);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service, campaignId, adGroupId]);

  const fetchSerp = async () => {
    if (!serpKeyword.trim()) return;
    setSerpBusy(true);
    setSerp(await service.getSerp(serpKeyword.trim(), store));
    setSerpBusy(false);
  };

  const fetchCreatives = async (domain: string) => {
    setCreativesFor(domain);
    setCreatives(null);
    setCreatives(await service.getTransparency(domain));
  };

  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;

  const movers: MoverStats[] = data?.movers ?? [];
  const you = movers.find(m => m.isYou);
  const competitors = movers.filter(m => !m.isYou);

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {competitors.length === 0 ? (
        <Alert severity="info">
          No auction-insights data for this scope yet. Upload a CSV export
          (segmented by week) to see who is gaining share here.
        </Alert>
      ) : (
        <>
          {you && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {adGroupId ? 'Share' : 'Average observed ad-group share'}: {pct(you.currentShare)} (last 2 complete wks) vs{' '}
              {pct(you.priorShare)} (prior 4 wks)
              {you.deltaPp != null && (
                <b style={{ color: you.deltaPp < 0 ? '#c62828' : '#2e7d32' }}>
                  {' '}
                  {you.deltaPp > 0 ? '+' : ''}
                  {you.deltaPp}pp
                </b>
              )}
            </Typography>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell align="right">{adGroupId ? 'Now' : 'Avg now'}</TableCell>
                <TableCell align="right">{adGroupId ? 'Prior' : 'Avg prior'}</TableCell>
                <TableCell align="right">{adGroupId ? 'Δpp' : 'Avg Δpp'}</TableCell>
                <TableCell align="right">Entities</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {competitors.slice(0, 12).map(m => (
                <TableRow key={m.domain} hover>
                  <TableCell>
                    {m.domain}{' '}
                    {m.isNewEntrant && (
                      <Chip label="new entrant" color="error" size="small" />
                    )}
                    {m.isReturning && (
                      <Chip label="returned" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">{pct(m.currentShare)}</TableCell>
                  <TableCell align="right">{pct(m.priorShare)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        (m.deltaPp ?? 0) > 0
                          ? 'error.main'
                          : (m.deltaPp ?? 0) < 0
                            ? 'success.main'
                            : undefined,
                      fontWeight: Math.abs(m.deltaPp ?? 0) >= 5 ? 700 : 400,
                    }}
                  >
                    {m.deltaPp == null ? '—' : `${m.deltaPp > 0 ? '+' : ''}${m.deltaPp}`}
                  </TableCell>
                  <TableCell align="right">{m.entities}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="View creatives (Ads Transparency Center)"
                      onClick={() => fetchCreatives(m.domain)}
                    >
                      <CollectionsIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
        <TextField
          size="small"
          label={`Live SERP keyword (${store})`}
          value={serpKeyword}
          onChange={e => setSerpKeyword(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="outlined"
          startIcon={<TravelExploreIcon />}
          onClick={fetchSerp}
          disabled={serpBusy || !serpKeyword.trim()}
        >
          {serpBusy ? 'Fetching…' : 'Check SERP'}
        </Button>
      </Stack>
      {serp && !serp.configured && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {serp.reason}
          {serp.searchUrl && (
            <>
              {' — '}
              <Link href={serp.searchUrl} target="_blank" rel="noreferrer">
                open this search yourself (geo-targeted)
              </Link>
            </>
          )}
        </Alert>
      )}
      {serp && serp.configured && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Ads on “{serp.query}” ({serp.store}){' '}
            {serp.googleUrl && (
              <Link href={serp.googleUrl} target="_blank" rel="noreferrer">
                open SERP
              </Link>
            )}
          </Typography>
          {serp.ads.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No ads returned on this sample.
            </Typography>
          ) : (
            serp.ads.map(ad => (
              <Typography key={`${ad.position}-${ad.domain}`} variant="body2">
                {ad.position}. <b>{ad.domain}</b> — {ad.title}
              </Typography>
            ))
          )}
          {serp.screenshot && (
            <Box sx={{ mt: 1, maxHeight: 420, overflow: 'auto', border: '1px solid #eee' }}>
              <img
                src={serp.screenshot}
                alt={`SERP for ${serp.query}`}
                style={{ width: '100%', display: 'block' }}
              />
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={creativesFor != null}
        onClose={() => setCreativesFor(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Creatives — {creativesFor}
          <IconButton
            onClick={() => setCreativesFor(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {creatives == null ? (
            <CircularProgress size={24} />
          ) : !creatives.configured ? (
            <Alert severity="warning">
              Automated lookup unavailable ({creatives.reason}).{' '}
              <Link
                href={`https://adstransparency.google.com/?domain=${encodeURIComponent(creativesFor ?? '')}`}
                target="_blank"
                rel="noreferrer"
              >
                Open {creativesFor} in the Google Ads Transparency Center
              </Link>{' '}
              to browse their running ads.
            </Alert>
          ) : creatives.creatives.length === 0 ? (
            <Typography variant="body2">No running creatives found.</Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {creatives.creatives.slice(0, 24).map((c, i) => (
                <Box key={i} sx={{ width: 220 }}>
                  {c.preview ? (
                    <img
                      src={c.preview}
                      alt={`${creativesFor} creative`}
                      style={{ maxWidth: '100%', borderRadius: 4 }}
                    />
                  ) : (
                    <Chip label={c.format} size="small" />
                  )}
                  <Typography variant="caption" display="block">
                    {c.format}
                    {c.lastShown ? ` · last shown ${c.lastShown}` : ''}
                    {c.detailsLink && (
                      <>
                        {' · '}
                        <Link href={c.detailsLink} target="_blank" rel="noreferrer">
                          details
                        </Link>
                      </>
                    )}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
